// LLM generated - Model Claude 4 Sonnet
import React, { useState, useEffect } from 'react';
import { TextInput, Textarea } from '@mantine/core';
import useTranslation from 'next-translate/useTranslation';

import Dialog from '~/domains/design-system/components/Dialog/Dialog';
import DialogContainer from '~/domains/design-system/components/Dialog/DialogContainer';
import { Button } from '~/domains/design-system/Button';
import { Text } from '~/domains/design-system/Text';
import type { PostModalProps } from '../types';
import { useStyles } from './PostModal.styles';

const MAX_CONTENT_LENGTH = 2000;
const MAX_TITLE_LENGTH = 100;

export default function PostModal({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting = false,
  isEditMode = false,
  initialTitle = '',
  initialContent = '',
  messageId,
  chatId,
}: PostModalProps) {
  const { classes } = useStyles();
  const { t } = useTranslation('chat');

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [notifyEntrants, setNotifyEntrants] = useState(false);
  const [focusedField, setFocusedField] = useState<'title' | 'content'>('content');

  // Initialize form when modal opens
  useEffect(() => {
    if (isOpen) {
      setTitle(initialTitle);
      setContent(initialContent);
      setNotifyEntrants(false);
    }
  }, [isOpen, initialTitle, initialContent]);

  // Calculate remaining characters based on focused field
  const titleRemainingChars = MAX_TITLE_LENGTH - title.length;
  const contentRemainingChars = MAX_CONTENT_LENGTH - content.length;
  const isTitleOverLimit = titleRemainingChars < 0;
  const isContentOverLimit = contentRemainingChars < 0;

  // Display counter based on focused field
  const displayedRemainingChars =
    focusedField === 'title' ? titleRemainingChars : contentRemainingChars;
  const isOverLimit = focusedField === 'title' ? isTitleOverLimit : isContentOverLimit;

  const handleSubmit = () => {
    if (title.trim() && content.trim() && !isTitleOverLimit && !isContentOverLimit && onSubmit) {
      onSubmit(title.trim(), content.trim(), notifyEntrants);
    }
  };

  const handleClose = () => {
    // Reset form state after closing (will happen after modal animation)
    setTitle('');
    setContent('');
    setNotifyEntrants(false);
    onClose();
  };

  return (
    <DialogContainer isOpen={isOpen}>
      <Dialog
        type={{
          desktop: 'modal',
          mobile: 'modal-full-screen',
        }}
        isOpen={isOpen}
        onClose={handleClose}
      >
        <div className={classes.container}>
          {/* Header */}
          <div className={classes.header}>
            <div className={classes.headerTop}>
              <div className={classes.counterContainer}>
                <Text variant="title-medium" color={isOverLimit ? 'red.6' : 'gray2.3'}>
                  {isOverLimit
                    ? `-${Math.abs(displayedRemainingChars).toLocaleString()}`
                    : displayedRemainingChars.toLocaleString()}
                </Text>
              </div>
              <div className={classes.titleContainer}>
                <h2 className={classes.title}>
                  {isEditMode ? t('postModal.editTitle') : t('postModal.title')}
                </h2>
              </div>
              <div className={classes.emptySpace} />
            </div>
          </div>

          {/* Content */}
          <div className={classes.content}>
            <TextInput
              placeholder={t('placeholders.postTitle')}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onFocus={() => setFocusedField('title')}
              disabled={isSubmitting}
              variant="unstyled"
              size="xl"
              data-autofocus
              classNames={{
                input: classes.titleInput,
              }}
            />
            <Textarea
              placeholder={t('placeholders.postContent')}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              onFocus={() => setFocusedField('content')}
              disabled={isSubmitting}
              variant="unstyled"
              minRows={4}
              autosize
              classNames={{
                input: classes.contentTextarea,
                wrapper: classes.textareaWrapper,
              }}
            />
          </div>

          {/* Footer */}
          <div className={classes.footer}>
            {/* Only show notify checkbox in create mode */}
            {!isEditMode && (
              <div className={classes.notificationToggle}>
                <input
                  type="checkbox"
                  id="notify-entrants"
                  checked={notifyEntrants}
                  onChange={(e) => setNotifyEntrants(e.target.checked)}
                  className={classes.checkbox}
                  disabled={isSubmitting}
                />
                <label htmlFor="notify-entrants" className={classes.checkboxLabel}>
                  {t('postModal.notifyEntrants')}
                </label>
              </div>
            )}
            <Button
              variant="secondary-fill"
              size="medium"
              onClick={handleSubmit}
              disabled={
                isSubmitting ||
                !title.trim() ||
                !content.trim() ||
                isTitleOverLimit ||
                isContentOverLimit
              }
              className={classes.postButton}
            >
              {isEditMode ? t('postModal.update') : t('postModal.post')}
            </Button>
          </div>
        </div>
      </Dialog>
    </DialogContainer>
  );
}
